import React from 'react';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import dayjs from 'dayjs';
import {
  FaAngleRight, FaBuilding, FaCalendarCheck, FaCar, FaLocationArrow, FaMapMarkerAlt, FaRoute,
} from 'react-icons/fa';
import { Modal, Badge, Button } from 'components/common';

const BADGE_MAP = { primary: 'primary', secondary: 'default', success: 'success', warning: 'warning', danger: 'danger', info: 'info' };

const MediaContent = ({ icon: Icon, heading, content, children }) => (
  <div className="tw-flex tw-gap-3 tw-mt-3">
    <span className="tw-flex-shrink-0 tw-w-8 tw-h-8 tw-rounded-xl tw-bg-blue-50 tw-text-blue-600 tw-flex tw-items-center tw-justify-center"><Icon /></span>
    <div className="tw-flex-1 tw-text-sm">
      <h6 className="tw-mb-1 tw-font-semibold tw-text-slate-700">{heading}</h6>
      {children || <p className="tw-mb-0 tw-text-slate-600">{content}</p>}
    </div>
  </div>
);
MediaContent.propTypes = { icon: PropTypes.elementType, heading: PropTypes.string, content: PropTypes.string, children: PropTypes.node };

const CalendarEventModal = ({ setIsOpenEventModal, isOpenEventModal, modalEventContent }) => {
  const handleClose = () => setIsOpenEventModal(false);

  if (!isOpenEventModal || !modalEventContent.event) return null;

  const { id, title, end, start } = modalEventContent.event;
  const {
    description, site_name, submited_by, submited_date, pickup_location, drop_location, status_name,
    bg_name, drivers, driver_name, driver_phone, car_name, route,
  } = modalEventContent.event.extendedProps;

  return (
    <Modal
      show={isOpenEventModal}
      onHide={handleClose}
      title={(
        <div>
          <h5 className="tw-mb-0 tw-font-semibold tw-text-slate-800">{title}</h5>
          <p className="tw-mb-0 tw-text-xs tw-mt-1 tw-italic tw-text-slate-500">Submited by <span className="tw-text-blue-600">{submited_by} | {submited_date}</span></p>
        </div>
      )}
      footer={(
        <Link to={`/booking/car/${id}`}>
          <Button size="sm" variant="primary" rightIcon={FaAngleRight}>See more details</Button>
        </Link>
      )}
    >
      <div className="tw-text-sm">
        <div className="tw-flex tw-justify-between">
          <p className="tw-mb-0">Booking ID#: <strong>{id}</strong></p>
          <p className="tw-mb-0">Status: <Badge variant={BADGE_MAP[bg_name] || 'default'} size="sm">{status_name}</Badge></p>
        </div>

        <MediaContent icon={FaMapMarkerAlt} heading="Pickup">
          <div>Time: <span className="tw-text-blue-600">{dayjs(start).format('hh:mm A')}</span></div>
          <div>Location: <span className="tw-text-blue-600">{pickup_location}</span></div>
        </MediaContent>

        <MediaContent icon={FaLocationArrow} heading="Destination">
          <span className="tw-text-blue-600 tw-font-semibold">{drop_location}</span>
        </MediaContent>

        <MediaContent icon={FaCalendarCheck} heading="Departure Date and Time">
          <div>Begin date: <span className="tw-text-blue-600">{dayjs(start).format('dddd, MMMM DD, YYYY, hh:mm A')}</span></div>
          <div>End date: <span className="tw-text-blue-600">{dayjs(end).format('dddd, MMMM DD, YYYY, hh:mm A')}</span></div>
        </MediaContent>

        <MediaContent icon={FaLocationArrow} heading="Purpose">
          <div style={{ whiteSpace: 'pre-line' }} dangerouslySetInnerHTML={{ __html: description }} />
        </MediaContent>

        {drivers && drivers.length > 0 && (
          <MediaContent icon={FaCar} heading="Car information">
            <ul className="tw-list-none tw-p-0 tw-m-0">
              {drivers.map((item) => (
                <li key={item.id} className="tw-mb-2 tw-pb-2 tw-border-b tw-border-slate-100">
                  <div>Car name: <span className="tw-text-blue-600">{item.car_name}</span></div>
                  <div>Driver: <span className="tw-text-blue-600">{item.driver_name}</span></div>
                  <div>Phone: <a href={`tel:${item.driver_phone}`} className="tw-text-blue-600">{item.driver_phone}</a></div>
                </li>
              ))}
            </ul>
          </MediaContent>
        )}

        {driver_name && (
          <MediaContent icon={FaCar} heading="Car information">
            <div>Car name: <span className="tw-text-blue-600">{car_name}</span></div>
            <div>Driver: <span className="tw-text-blue-600">{driver_name}</span></div>
            <div>Phone: <a href={`tel:${driver_phone}`} className="tw-text-blue-600">{driver_phone}</a></div>
          </MediaContent>
        )}

        <MediaContent icon={FaRoute} heading="Route" content={route} />
        <MediaContent icon={FaBuilding} heading="Site office" content={site_name} />
      </div>
    </Modal>
  );
};

CalendarEventModal.propTypes = {
  setIsOpenEventModal: PropTypes.func.isRequired,
  isOpenEventModal: PropTypes.bool.isRequired,
  modalEventContent: PropTypes.object.isRequired,
};

export default CalendarEventModal;
